import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

import googleEmilie from "@/assets/testimonials/google-emilie.jpg";
import googleCeline from "@/assets/testimonials/google-celine.jpg";
import googleAline from "@/assets/testimonials/google-aline.jpg";
import googlePatricia from "@/assets/testimonials/google-patricia.jpg";
import googleAudrey from "@/assets/testimonials/google-audrey.jpg";
import googleCamille from "@/assets/testimonials/google-camille.jpg";
import googleNadege from "@/assets/testimonials/google-nadege.jpg";

import t01 from "@/assets/testimonials/t01.jpg";
import t02 from "@/assets/testimonials/t02.jpg";
import t03 from "@/assets/testimonials/t03.jpg";
import t04 from "@/assets/testimonials/t04.jpg";
import t05 from "@/assets/testimonials/t05.jpg";
import t06 from "@/assets/testimonials/t06.jpg";
import t07 from "@/assets/testimonials/t07.jpg";
import t08 from "@/assets/testimonials/t08.jpg";
import t09 from "@/assets/testimonials/t09.jpg";
import t10 from "@/assets/testimonials/t10.jpg";
import t11 from "@/assets/testimonials/t11.jpg";
import t12 from "@/assets/testimonials/t12.jpg";
import t13 from "@/assets/testimonials/t13.jpg";
import t14 from "@/assets/testimonials/t14.jpg";
import t15 from "@/assets/testimonials/t15.jpg";
import t16 from "@/assets/testimonials/t16.jpg";
import t17 from "@/assets/testimonials/t17.jpg";
import t18 from "@/assets/testimonials/t18.jpg";
import t19 from "@/assets/testimonials/t19.jpg";
import t20 from "@/assets/testimonials/t20.jpg";

export interface Testimonial {
  name: string;
  date: string;
  text: string;
  image: string;
  isGoogle?: boolean;
}

/* ── Avis Google (vrais avis GMB) ── */
const googleTestimonials: Testimonial[] = [
  {
    name: "Emilie H.",
    date: "Mars 2024",
    text: "Je recommande vivement Nathalie. Grâce à elle et son accompagnement, j'ai pu arrêter de fumer. Les séances m'ont aidées à surmonter mon appréhension, à me donner confiance dans ma capacité à réussir à me détacher du tabac. Un grand merci à Nathalie pour son investissement et le soin qu'elle met dans sa pratique.",
    image: googleEmilie,
    isGoogle: true,
  },
  {
    name: "Céline M.",
    date: "Février 2024",
    text: "Après avoir suivi des séances d'hypnothérapies, je tiens à partager l'impact significatif qu'elles ont eu sur ma vie personnelle et professionnelle. Cela m'a permis d'acquérir une prise de conscience profonde transformant ainsi mon regard sur mon quotidien et mes actions. Nathalie est une très belle personne, compétente et bienveillante que je recommande à tout le monde.",
    image: googleCeline,
    isGoogle: true,
  },
  {
    name: "Aline L.",
    date: "Janvier 2024",
    text: "Une expérience à vivre, une personne à rencontrer. D'une bienveillance et gentillesse remarquable, une joie de vivre et une sérénité communicative. Dans tous les cas, sa rencontre nous fait changer en bien. Je recommande. Merci Nathalie.",
    image: googleAline,
    isGoogle: true,
  },
  {
    name: "Patricia H.",
    date: "Mars 2024",
    text: "Elle est vraiment formidable, j'étais au fond du gouffre. Elle m'a aidé à reprendre confiance en moi et effacer de très mauvais souvenirs et elle m'a aidé à m'enlever une grosse phobie. Je la recommande vivement, elle est vraiment géniale. Je te remercie vraiment Nathalie.",
    image: googlePatricia,
    isGoogle: true,
  },
  {
    name: "Audrey C.",
    date: "Novembre 2020",
    text: "Praticienne à l'écoute et disponible. Les séances m'ont permis de me sentir mieux, d'aller mieux et enfin de réussir à avancer en laissant de côté tout les soucis ! Je recommande les yeux fermés.",
    image: googleAudrey,
    isGoogle: true,
  },
  {
    name: "Camille B.",
    date: "Novembre 2023",
    text: "Je remercie infiniment Nathalie de m'avoir aidé à prendre conscience que la vie est belle et précieuse. Je recommande à 100%.",
    image: googleCamille,
    isGoogle: true,
  },
  {
    name: "Nadège D.",
    date: "Octobre 2023",
    text: "Je suis partie après la séance rassurée pour mon avenir et plus sereine et apaisée. Je recommande, elle est formidable. N'hésitez pas.",
    image: googleNadege,
  },
];

/* ── Autres témoignages ── */
const otherTestimonials: Testimonial[] = [
  {
    name: "Karim B.",
    date: "Février 2024",
    text: "Je fumais depuis 20 ans, j'avais tout essayé. Avec Nathalie, en quelques séances seulement, j'ai compris pourquoi je fumais. Aujourd'hui 6 mois sans tabac, sans manque. Merci infiniment.",
    image: t02,
  },
  {
    name: "Fatou D.",
    date: "Janvier 2024",
    text: "J'étais sceptique au début mais Nathalie m'a mise en confiance dès le premier appel. L'hypnose a changé ma façon de voir la cigarette. Je suis libre, et ça fait un bien fou !",
    image: t03,
  },
  {
    name: "Thomas R.",
    date: "Mars 2024",
    text: "L'approche de Nathalie est unique. Pas de pression, pas de jugement. Elle m'a aidé à comprendre mes mécanismes et aujourd'hui je ne fume plus depuis 4 mois. Je recommande à 100%.",
    image: t04,
  },
  {
    name: "Véronique M.",
    date: "Décembre 2023",
    text: "Après 30 ans de tabac, je pensais que c'était impossible. Nathalie m'a prouvé le contraire avec une douceur et un professionnalisme remarquables. Ma vie a changé.",
    image: t05,
  },
  {
    name: "Kevin L.",
    date: "Novembre 2023",
    text: "Super expérience ! Nathalie est à l'écoute et bienveillante. L'accompagnement en visio est top, j'ai pu suivre les séances depuis chez moi. Résultat : plus de tabac depuis 3 mois.",
    image: t06,
  },
  {
    name: "Samira K.",
    date: "Février 2024",
    text: "Nathalie a cette capacité rare de vous mettre à l'aise immédiatement. Son accompagnement m'a permis de décrocher sans stress. Je la recommande les yeux fermés.",
    image: t07,
  },
  {
    name: "Julien P.",
    date: "Janvier 2024",
    text: "J'ai arrêté de fumer grâce à Nathalie après 15 ans de tabagisme. Ce qui m'a bluffé c'est que je n'ai eu aucune envie de reprendre. La méthode est vraiment efficace.",
    image: t08,
  },
  {
    name: "Nadia A.",
    date: "Mars 2024",
    text: "Une personne extraordinaire qui sait trouver les mots justes. L'hypnose m'a aidée à me libérer d'un poids que je portais depuis des années. Merci Nathalie pour tout.",
    image: t09,
  },
  {
    name: "Bernard G.",
    date: "Octobre 2023",
    text: "À 62 ans, je ne pensais plus pouvoir arrêter. L'accompagnement de Nathalie m'a prouvé qu'il n'est jamais trop tard. Un an sans tabac et je me sens revivre. Merci !",
    image: t10,
  },
  {
    name: "Moussa T.",
    date: "Décembre 2023",
    text: "Nathalie est passionnée par ce qu'elle fait et ça se ressent. Elle m'a accompagné avec patience et bienveillance. Aujourd'hui je suis non-fumeur et fier de l'être.",
    image: t11,
  },
  {
    name: "Claire D.",
    date: "Novembre 2023",
    text: "Les séances en visio sont très pratiques et tout aussi efficaces. Nathalie crée un vrai lien de confiance. J'ai arrêté sereinement, sans compensation alimentaire. Top !",
    image: t12,
  },
  {
    name: "Philippe C.",
    date: "Janvier 2024",
    text: "J'avais déjà essayé les patchs, la vape, rien ne marchait. L'approche de Nathalie est différente, elle va à la racine du problème. Ça fait 5 mois que je suis libéré.",
    image: t13,
  },
  {
    name: "Priya S.",
    date: "Février 2024",
    text: "Merci Nathalie pour cet accompagnement sur mesure. Vous avez su adapter vos séances à mon rythme et mes besoins. Je me sens enfin libre et en paix avec moi-même.",
    image: t14,
  },
  {
    name: "Marco V.",
    date: "Mars 2024",
    text: "Fumeur depuis 25 ans, je n'y croyais plus. Nathalie a réussi là où tout le reste avait échoué. Son approche en douceur change tout. Je recommande vivement.",
    image: t15,
  },
  {
    name: "Isabelle F.",
    date: "Décembre 2023",
    text: "La bienveillance de Nathalie est communicative. Elle m'a aidée à changer ma relation au tabac en profondeur. Plus de culpabilité, plus de lutte. Juste la liberté.",
    image: t16,
  },
  {
    name: "Rayan M.",
    date: "Novembre 2023",
    text: "J'ai 24 ans et je fumais depuis l'adolescence. Grâce à Nathalie j'ai compris que ce n'était pas qu'une question de volonté. Son accompagnement est complet et humain.",
    image: t17,
  },
  {
    name: "Mei-Lin W.",
    date: "Janvier 2024",
    text: "Nathalie est une professionnelle exceptionnelle. Elle combine hypnose et coaching avec beaucoup de finesse. J'ai arrêté sans stress ni prise de poids. Merci !",
    image: t18,
  },
  {
    name: "Josiane E.",
    date: "Février 2024",
    text: "À toutes celles et ceux qui hésitent : foncez ! Nathalie a transformé ma vie. Plus d'essoufflement, plus de mauvaise haleine, et surtout une fierté immense. Merci du fond du cœur.",
    image: t19,
  },
  {
    name: "Lucas N.",
    date: "Mars 2024",
    text: "Le module e-learning combiné aux séances individuelles, c'est vraiment la formule gagnante. Nathalie est disponible et rassurante. 3 mois sans tabac, aucune envie de reprendre.",
    image: t20,
  },
];

export const defaultTestimonials: Testimonial[] = [
  ...googleTestimonials,
  ...otherTestimonials.filter((testimonial) => !/visio|e-learning/i.test(testimonial.text)),
];

interface TestimonialCarouselProps {
  testimonials?: Testimonial[];
  title?: string;
  subtitle?: string;
}

const TestimonialCarousel = ({
  testimonials = defaultTestimonials,
  title = "Leurs expériences avec mon accompagnement",
  subtitle = "Témoignages",
}: TestimonialCarouselProps) => {
  const [current, setCurrent] = useState(0);
  const total = testimonials.length;

  useEffect(() => {
    const timer = setInterval(() => setCurrent((c) => (c + 1) % total), 6000);
    return () => clearInterval(timer);
  }, [total]);

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);
  const t = testimonials[current];

  const getVisibleThumbnails = (max: number) => {
    if (total <= max) return testimonials.map((_, i) => i);
    const half = Math.floor(max / 2);
    const indices: number[] = [];
    for (let offset = -half; offset <= half; offset++) {
      indices.push((current + offset + total) % total);
    }
    return indices;
  };

  const desktopIndices = getVisibleThumbnails(7);
  const mobileIndices = getVisibleThumbnails(5);

  return (
    <section className="py-20 px-4 sm:px-6 bg-background overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-widest mb-2">
            {subtitle}
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
            {title}
          </h2>
        </motion.div>

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.97 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-card rounded-2xl p-8 md:p-10 border border-border shadow-lg relative"
            >
              <Quote size={40} className="text-accent/20 absolute top-6 right-8" />

              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.15, duration: 0.4 }}
                  className="shrink-0 overflow-hidden rounded-full w-20 h-20 md:w-24 md:h-24 border-4 border-accent/30 shadow-md"
                >
                  <img src={t.image} alt={t.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </motion.div>

                <div className="flex-1 text-center md:text-left">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="flex gap-1 justify-center md:justify-start mb-4"
                  >
                    {[...Array(5)].map((_, i) => (
                      <motion.div key={i} initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.25 + i * 0.08 }}>
                        <Star size={18} className="text-accent fill-accent" />
                      </motion.div>
                    ))}
                  </motion.div>

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="font-body text-foreground/80 text-base md:text-lg leading-relaxed italic mb-6"
                  >
                    "{t.text}"
                  </motion.p>

                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                    <p className="font-heading text-lg font-bold text-foreground">{t.name}</p>
                    <p className="font-body text-muted-foreground text-sm">
                      {t.isGoogle ? "Avis Google — " : ""}{t.date}
                    </p>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <button
            onClick={prev}
            aria-label="Avis précédent"
            className="absolute -left-3 md:-left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-foreground/60 hover:text-foreground hover:shadow-lg hover:scale-110 transition-all"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            aria-label="Avis suivant"
            className="absolute -right-3 md:-right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-foreground/60 hover:text-foreground hover:shadow-lg hover:scale-110 transition-all"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="flex items-center justify-center gap-3 mt-8">
          {desktopIndices.map((i) => {
            const isMobileHidden = !mobileIndices.includes(i);
            return (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`transition-all duration-300 rounded-full overflow-hidden ${
                  isMobileHidden ? "hidden sm:block" : ""
                } ${
                  i === current
                    ? "ring-2 ring-accent ring-offset-2 ring-offset-background scale-110"
                    : "opacity-50 hover:opacity-80 hover:scale-105"
                }`}
                aria-label={`Voir l'avis de ${testimonials[i].name}`}
              >
                <img src={testimonials[i].image} alt={testimonials[i].name} loading="lazy" decoding="async" className="w-10 h-10 rounded-full object-cover" />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
